/**
 * Document Export Utilities
 * Parses markdown documents into structured JSON and provides download helpers.
 */

import type { SavedDocument } from '@/lib/types';

interface ExportedSection {
  heading: string;
  level: number;
  content: string[];
}

interface ExportedRequirement {
  id: string;
  title: string;
  priority: string;
  description: string;
  acceptanceCriteria: string[];
}

interface ExportedChecklistItem {
  text: string;
  checked: boolean;
  category?: string;
}

interface ExportedScenario {
  name: string;
  given: string;
  when: string;
  then: string;
}

export interface ExportedDocument {
  title: string;
  createdAt: string;
  updatedAt: string;
  templateType: string;
  category: string;
  projectName?: string;
  sections: ExportedSection[];
  requirements?: ExportedRequirement[];
  checklist?: ExportedChecklistItem[];
  scenarios?: ExportedScenario[];
}

/**
 * Parse markdown content into structured JSON.
 */
export function exportDocumentAsJson(content: string, metadata: SavedDocument): ExportedDocument {
  const lines = content.split('\n');
  const sections: ExportedSection[] = [];
  const requirements: ExportedRequirement[] = [];
  const checklist: ExportedChecklistItem[] = [];
  const scenarios: ExportedScenario[] = [];

  let title = '';
  let currentSection: ExportedSection | null = null;
  let currentCategory = '';

  for (const line of lines) {
    const trimmed = line.trim();

    // Extract title (H1)
    if (trimmed.startsWith('# ') && !title) {
      title = trimmed.slice(2).trim();
      continue;
    }

    // Section headings
    if (trimmed.startsWith('## ')) {
      currentSection = { heading: trimmed.slice(3).trim(), level: 2, content: [] };
      sections.push(currentSection);
      currentCategory = currentSection.heading;
      continue;
    }
    if (trimmed.startsWith('### ')) {
      currentSection = { heading: trimmed.slice(4).trim(), level: 3, content: [] };
      sections.push(currentSection);
      continue;
    }

    // Checkboxes
    if (trimmed.startsWith('- [ ] ') || trimmed.startsWith('- [x] ')) {
      const isChecked = trimmed.startsWith('- [x] ');
      checklist.push({
        text: trimmed.slice(6).trim(),
        checked: isChecked,
        category: currentCategory || undefined,
      });
      currentSection?.content.push(trimmed);
      continue;
    }

    // Requirements (FR-xxx, NFR-xxx patterns)
    const reqMatch = trimmed.match(/^(?:###?\s+)?(FR-\d+|NFR-[A-Z]*\d+):\s*(.+)/);
    if (reqMatch) {
      requirements.push({
        id: reqMatch[1],
        title: reqMatch[2].trim(),
        priority: '',
        description: '',
        acceptanceCriteria: [],
      });
      continue;
    }

    // BDD Scenarios
    const scenarioMatch = trimmed.match(/^###?\s+Scenario:\s*(.+)/);
    if (scenarioMatch) {
      scenarios.push({
        name: scenarioMatch[1].trim(),
        given: '',
        when: '',
        then: '',
      });
      continue;
    }

    // Given/When/Then for current scenario
    if (scenarios.length > 0) {
      const current = scenarios[scenarios.length - 1];
      const givenMatch = trimmed.match(/^\*?\*?Given\*?\*?\s+(.+)/);
      const whenMatch = trimmed.match(/^\*?\*?When\*?\*?\s+(.+)/);
      const thenMatch = trimmed.match(/^\*?\*?Then\*?\*?\s+(.+)/);
      if (givenMatch) { current.given = givenMatch[1]; continue; }
      if (whenMatch) { current.when = whenMatch[1]; continue; }
      if (thenMatch) { current.then = thenMatch[1]; continue; }
    }

    // Regular content
    if (trimmed && currentSection) {
      currentSection.content.push(trimmed);
    }
  }

  const result: ExportedDocument = {
    title: title || metadata.title,
    createdAt: metadata.createdAt,
    updatedAt: metadata.updatedAt,
    templateType: metadata.templateType,
    category: metadata.category,
    projectName: metadata.projectName,
    sections,
  };

  if (requirements.length > 0) result.requirements = requirements;
  if (checklist.length > 0) result.checklist = checklist;
  if (scenarios.length > 0) result.scenarios = scenarios;

  return result;
}

/**
 * Download an exported document as a JSON file.
 */
export function downloadAsJson(doc: ExportedDocument, title: string): void {
  const json = JSON.stringify(doc, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  downloadBlob(blob, sanitizeFilename(title) + '.json');
}

/**
 * Download markdown content as a .md file.
 */
export function downloadAsMarkdown(content: string, title: string): void {
  const blob = new Blob([content], { type: 'text/markdown' });
  downloadBlob(blob, sanitizeFilename(title) + '.md');
}

/**
 * Download plain text content as a .txt file.
 */
export function downloadAsText(content: string, title: string): void {
  const blob = new Blob([content], { type: 'text/plain' });
  downloadBlob(blob, sanitizeFilename(title) + '-transcription.txt');
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function sanitizeFilename(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 100) || 'document';
}
